import type { DraftPlayerResponse } from '@on3/api';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import { IndustryComparisonDraftFourServicesItem } from './IndustryComparisonDraftFourServicesItem';
import styles from './IndustryComparisonDraftList.module.scss';
import { IndustryComparisonDraftListColumnHeader } from './IndustryComparisonDraftListColumnHeader';
import { IndustryComparisonDraftPlayerItem } from './IndustryComparisonDraftPlayerItem';

interface IndustryComparisonDraftListProps {
  playerList?: DraftPlayerResponse[] | null;
}

type SelectedOptions =
  | 'Consensus'
  | 'On3'
  | 'Rivals'
  | 'Industry'
  | '247'
  | 'Espn';

const useSortList = (list?: DraftPlayerResponse[] | null) =>
  useCallback(
    (sortBy: SelectedOptions | string, ascending: boolean) => {
      if (!list?.length) {
        return list;
      }

      const isPick = sortBy === 'pick';
      const isPlayer = sortBy === 'lastName';
      const sortedList = [...list]?.sort((a, b) => {
        const setIndex =
          a.industryComparison?.findIndex((i) => i.type === sortBy) || 0;
        let setA = null;
        let setB = null;

        if (isPick) {
          setA = a?.pick;
          setB = b?.pick;
        } else if (isPlayer) {
          setA = a.player?.[sortBy];
          setB = b.player?.[sortBy];
        } else {
          const setAobject = a.player && a.industryComparison?.[setIndex];
          const setBobject = b.player && b.industryComparison?.[setIndex];

          setA = setAobject?.rating;
          setB = setBobject?.rating;
        }

        //  puts nulls at the end of list
        if (!setA && setA !== 0) {
          return 1;
        }

        if (!setB && setB !== 0) {
          return -1;
        }

        if (setA < setB) {
          return ascending ? 1 : -1;
        }

        if (setA > setB) {
          return ascending ? -1 : 1;
        }

        return 0;
      });

      return sortedList;
    },
    [list],
  );

export const IndustryComparisonDraftList = ({
  playerList,
}: IndustryComparisonDraftListProps) => {
  const router = useRouter();
  const { round } = router.query;
  const [list, setList] = useState(playerList);

  const [selected, setSelected] = useState<SelectedOptions | string>('pick');
  const [ascending, setAscending] = useState(false);

  const [disabled, setDisabled] = useState(false);

  const sortList = useSortList(playerList);

  const handleChange: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      setDisabled(true);
      const { dataset } = e.currentTarget;
      const { sorttype, flip = 'false' } = dataset;
      const sorttypeKey = sorttype as SelectedOptions | string;
      const isFlipped = flip === 'true';
      let orderBy = ascending;

      if (sorttypeKey !== selected) {
        setSelected(sorttypeKey);
        orderBy = isFlipped;
        setAscending(isFlipped);
      } else {
        orderBy = !ascending;
        setAscending((prev) => !prev);
      }

      const sortedList = sortList(sorttypeKey, orderBy);

      sortedList && setList(sortedList);
      setDisabled(false);
    },
    [ascending, selected, sortList],
  );

  return (
    <>
      {!!list?.length && (
        <IndustryComparisonDraftListColumnHeader
          ascending={ascending}
          disabled={disabled}
          onChange={handleChange}
          selected={selected}
        />
      )}
      <ol className={styles.listWrapper}>
        {!!list?.length &&
          list?.map((item, index) => {
            return (
              <li
                className={styles.industryComparisonItemContainer}
                key={`${item.key}-${index}-${ascending}-${selected}-${round}-industry-comparison`}
              >
                <IndustryComparisonDraftPlayerItem
                  {...item}
                  className={styles.industryComparisonPlayerItem}
                  selected={selected === 'Industry'}
                />

                <IndustryComparisonDraftFourServicesItem
                  className={styles.industryComparisonFourServicesItem}
                  psoKey={item.player?.key}
                  ratings={item.industryComparison}
                  selected={selected}
                  slug={item.player?.slug}
                />
              </li>
            );
          })}
        {!list?.length && (
          <Text className={styles.noListText} component="li">
            There are no players for this selection.
          </Text>
        )}
      </ol>
    </>
  );
};
